import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';
import {
  CLIENT_DIAGNOSTIC_EVENT_TYPE_OPTIONS,
  getClientDiagnosticEventTypeBadgeClass,
  getClientDiagnosticEventTypeLabel,
  getLabelledDiagnosticEventLabel,
  isChargedNoOrderEvent,
  isClientDiagnosticErrorEvent,
  isLabelledDiagnosticEvent
} from '../pos-diagnostics-event-types';

@Component({
  selector: 'app-pos-diagnostics-list',
  templateUrl: './pos-diagnostics-list.component.html',
  styleUrls: ['./pos-diagnostics-list.component.scss']
})
export class PosDiagnosticsListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() storeId: string = '';
  @Output() viewSession = new EventEmitter<string>();

  events: any[] = [];
  errorEvents: any[] = [];
  labelledEvents: any[] = [];
  isLoading = false;
  errorMessage = '';
  endpointUnavailable = false;

  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  pageSize = 50;

  dateFrom = '';
  dateTo = '';
  selectedLevel = '';
  selectedEventType = '';
  buildCommit = '';
  terminalId = '';
  searchText = '';

  showDetail = false;
  selectedEvent: any = null;
  loadingDetail = false;
  detailError = '';

  autoRefreshEnabled = true;
  refreshIntervalSec = 30;
  countdown = 0;
  private refreshSubscription?: Subscription;
  private countdownSub?: Subscription;
  private loadSeq = 0;
  private silentLoadSeq = 0;
  private detailLoadSeq = 0;

  Math = Math;

  levelOptions = [
    { value: '', label: 'All levels' },
    { value: 'error', label: 'Error' },
    { value: 'warn', label: 'Warn' },
    { value: 'info', label: 'Info' }
  ];

  eventTypeOptions = CLIENT_DIAGNOSTIC_EVENT_TYPE_OPTIONS;

  pageSizeOptions = [20, 50, 100, 200];

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadEvents();
    }
    this.countdown = this.refreshIntervalSec;
    this.startAutoRefresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange) {
      this.resetForStoreChange();
      if (this.storeId) {
        this.loadEvents();
      }
    }
  }

  private resetForStoreChange(): void {
    this.loadSeq++;
    this.silentLoadSeq++;
    this.isLoading = false;
    this.detailLoadSeq++;
    this.closeDetail();
    this.events = [];
    this.errorEvents = [];
    this.labelledEvents = [];
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalItems = 0;
    this.errorMessage = '';
    this.endpointUnavailable = false;
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.countdown = this.refreshIntervalSec;

    this.refreshSubscription = interval(this.refreshIntervalSec * 1000).subscribe(() => {
      if (this.storeId && !this.showDetail) {
        this.loadEventsSilent();
      }
      this.countdown = this.refreshIntervalSec;
    });

    this.countdownSub = interval(1000).subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      }
    });
  }

  stopAutoRefresh(): void {
    this.refreshSubscription?.unsubscribe();
    this.countdownSub?.unsubscribe();
    this.refreshSubscription = undefined;
    this.countdownSub = undefined;
  }

  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  onRefreshIntervalChange(): void {
    this.countdown = this.refreshIntervalSec;
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  }

  onPageSizeChange(): void {
    if (this.pageSize > 200) {
      this.pageSize = 200;
    }
    this.currentPage = 1;
    this.loadEvents();
  }

  private buildQueryParams(): any {
    const params: any = {
      page: this.currentPage,
      limit: Math.min(this.pageSize, 200)
    };

    if (this.selectedLevel) params.level = this.selectedLevel;
    if (this.selectedEventType) params.eventType = this.selectedEventType;
    if (this.dateFrom) params.dateFrom = this.dateFrom;
    if (this.dateTo) params.dateTo = this.dateTo;
    if (this.buildCommit?.trim()) params.buildCommit = this.buildCommit.trim();
    if (this.terminalId?.trim()) params.terminalId = this.terminalId.trim();
    if (this.searchText?.trim()) params.search = this.searchText.trim();

    return params;
  }

  loadEvents(): void {
    this.silentLoadSeq++;
    const seq = ++this.loadSeq;
    const requestedStoreId = this.storeId;
    this.isLoading = true;
    this.errorMessage = '';
    this.endpointUnavailable = false;

    this.ecommerceService.getClientDiagnostics(requestedStoreId, this.buildQueryParams()).pipe(
      finalize(() => {
        if (seq === this.loadSeq) {
          this.isLoading = false;
        }
      })
    ).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) return;
        this.applyEventsResponse(res);
      },
      error: (error: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) return;
        this.handleListError(error);
      }
    });
  }

  private loadEventsSilent(): void {
    if (this.isLoading) return;
    const seq = ++this.silentLoadSeq;
    const userSeq = this.loadSeq;
    const requestedStoreId = this.storeId;

    this.ecommerceService.getClientDiagnostics(requestedStoreId, this.buildQueryParams()).subscribe({
      next: (res: any) => {
        if (seq !== this.silentLoadSeq || userSeq !== this.loadSeq || requestedStoreId !== this.storeId) return;
        this.applyEventsResponse(res);
      },
      error: () => {
        if (seq !== this.silentLoadSeq || userSeq !== this.loadSeq || requestedStoreId !== this.storeId) return;
      }
    });
  }

  private applyEventsResponse(res: any): void {
    const data = res?.response?.data ?? res?.data ?? null;
    if (!data) {
      this.events = [];
      this.errorEvents = [];
      this.labelledEvents = [];
      return;
    }

    this.events = data.events || [];
    this.partitionEvents(this.events);
    const pagination = data.pagination || {};
    this.totalItems = pagination.total ?? 0;
    this.totalPages = pagination.pages ?? 1;
    this.currentPage = pagination.page ?? this.currentPage;
  }

  private partitionEvents(events: any[]): void {
    this.labelledEvents = events.filter(event => isLabelledDiagnosticEvent(event));
    this.errorEvents = events.filter(event => !isLabelledDiagnosticEvent(event));
  }

  get errorEventsOnPage(): number {
    return this.errorEvents.filter(event => isClientDiagnosticErrorEvent(event)).length;
  }

  get labelledEventsOnPage(): number {
    return this.labelledEvents.length;
  }

  private handleListError(error: any): void {
    const status = error?.status;
    if (status === 404) {
      this.endpointUnavailable = true;
      this.errorMessage = 'Diagnostics API is not deployed yet. This view will populate automatically once the backend is live.';
      this.events = [];
      return;
    }
    this.errorMessage = error?.error?.response?.message || 'Failed to load diagnostics events';
    this.toastr.error(this.errorMessage);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadEvents();
  }

  resetFilters(): void {
    this.dateFrom = '';
    this.dateTo = '';
    this.selectedLevel = '';
    this.selectedEventType = '';
    this.buildCommit = '';
    this.terminalId = '';
    this.searchText = '';
    this.currentPage = 1;
    this.loadEvents();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEvents();
  }

  viewEventDetail(event: any): void {
    this.stopAutoRefresh();
    this.selectedEvent = event;
    this.showDetail = true;
    this.loadingDetail = true;
    this.detailError = '';

    const eventId = event._id || event.id;
    if (!eventId) {
      this.loadingDetail = false;
      return;
    }

    const seq = ++this.detailLoadSeq;
    const requestedStoreId = this.storeId;

    this.ecommerceService.getClientDiagnosticDetail(requestedStoreId, eventId).subscribe({
      next: (res: any) => {
        if (seq !== this.detailLoadSeq || requestedStoreId !== this.storeId) return;
        const data = res?.response?.data ?? res?.data ?? {};
        this.selectedEvent = data.event || data;
        this.loadingDetail = false;
      },
      error: (error: any) => {
        if (seq !== this.detailLoadSeq || requestedStoreId !== this.storeId) return;
        this.loadingDetail = false;
        if (error?.status === 404) {
          this.detailError = 'Detail endpoint not available yet — showing list row data only.';
        } else {
          this.detailError = error?.error?.response?.message || 'Failed to load event detail';
          this.toastr.error(this.detailError);
        }
      }
    });
  }

  closeDetail(): void {
    this.showDetail = false;
    this.selectedEvent = null;
    this.detailError = '';
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  }

  openSessionTimeline(sessionId: string): void {
    if (sessionId) {
      this.viewSession.emit(sessionId);
    }
  }

  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'error': return 'bg-danger';
      case 'warn': return 'bg-warning text-dark';
      case 'info': return 'bg-info text-dark';
      default: return 'bg-secondary';
    }
  }

  isErrorLevel(level: string): boolean {
    return level === 'error';
  }

  isCriticalFinancialEvent(event: any): boolean {
    return isChargedNoOrderEvent(event?.eventType);
  }

  getEventTypeLabel(eventType: string): string {
    return getClientDiagnosticEventTypeLabel(eventType);
  }

  getLabelledEventLabel(event: any): string {
    return getLabelledDiagnosticEventLabel(event);
  }

  isLabelledEvent(event: any): boolean {
    return isLabelledDiagnosticEvent(event);
  }

  getEventTypeBadgeClass(eventType: string): string {
    return getClientDiagnosticEventTypeBadgeClass(eventType);
  }

  getStackTrace(event: any): any {
    return event?.stackTrace || event?.symbolicatedStack || event?.stack || null;
  }

  getStackFrames(event: any): any[] {
    const trace = this.getStackTrace(event);
    if (!trace) return [];
    if (Array.isArray(trace.frames)) return trace.frames;
    if (Array.isArray(trace)) return trace;
    return [];
  }

  isSymbolicated(event: any): boolean {
    const trace = this.getStackTrace(event);
    return trace?.symbolicated === true;
  }

  getRawStack(event: any): string {
    const trace = this.getStackTrace(event);
    if (typeof trace?.raw === 'string') return trace.raw;
    if (typeof event?.stack === 'string') return event.stack;
    return '';
  }

  getBreadcrumbs(event: any): any[] {
    const crumbs = event?.breadcrumbs;
    return Array.isArray(crumbs) ? [...crumbs].sort((a, b) =>
      new Date(a.at).getTime() - new Date(b.at).getTime()
    ) : [];
  }

  getLogTail(event: any): string[] {
    return Array.isArray(event?.logTail) ? event.logTail : [];
  }

  getContext(event: any): any {
    return event?.context || {};
  }

  getCartSnapshot(event: any): any {
    const ctx = this.getContext(event);
    return ctx?.cartSnapshot || event?.cartSnapshot || null;
  }

  getBreadcrumbIcon(kind: string): string {
    switch (kind) {
      case 'nav': return 'navigation-2-outline';
      case 'api': return 'globe-outline';
      case 'action': return 'flash-outline';
      case 'state': return 'layers-outline';
      default: return 'radio-button-on-outline';
    }
  }

  formatCurrency(total: number): string {
    if (total == null) return '—';
    try {
      return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(total);
    } catch {
      return `${total} DKK`;
    }
  }

  prettyJson(value: any): string {
    if (value == null) return '—';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  trackByEventId(index: number, event: any): string {
    return event._id || event.id || String(index);
  }
}
